package com.merkost.handpass.admin.pages

import androidx.compose.runtime.*
import com.merkost.handpass.admin.auth.LocalAdminAuth
import com.merkost.handpass.admin.components.*
import com.merkost.handpass.admin.container.QueueContainer
import com.merkost.handpass.admin.container.StatsContainer
import com.merkost.handpass.admin.contract.*
import com.merkost.handpass.api.dto.admin.AdminQueueItem
import com.merkost.handpass.api.enums.AdminRole
import com.merkost.handpass.sharedweb.components.WebIcon
import com.merkost.handpass.sharedweb.components.Icon
import com.merkost.handpass.sharedweb.theme.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import com.merkost.handpass.admin.di.koinContainer
import com.merkost.handpass.admin.util.LocalViewportMode
import com.merkost.handpass.admin.util.ViewportMode
import pro.respawn.flowmvi.compose.dsl.subscribe

@Page("/")
@Composable
fun QueuePage() {
    val ctx = rememberPageContext()
    AdminPageScaffold(title = "Report Queue", currentPath = "/", showTitle = false) {
        val container = koinContainer<QueueContainer>()
        var pendingConfirm by remember { mutableStateOf<Pair<QueueBulkActionKind, ConfirmConfig>?>(null) }
        var saveViewName by remember { mutableStateOf<String?>(null) }

        LaunchedEffect(Unit) {
            val initialSource = ctx.route.params["source"]?.let { runCatching { QueueSourceFilter.valueOf(it) }.getOrNull() }
            if (initialSource != null) container.store.intent(QueueIntent.SetSource(initialSource))
        }

        with(container.store) {
            val state by subscribe { action ->
                when (action) {
                    is QueueAction.Navigate -> navigate(action.path)
                    is QueueAction.OpenBulkConfirm -> pendingConfirm = action.kind to bulkConfirmConfig(action.kind, action.itemCount)
                    is QueueAction.ShowSuccess -> toast(action.message, ToastKind.Success)
                    is QueueAction.ShowError -> toast(action.message, ToastKind.Error)
                    is QueueAction.ShowPartialSuccess -> toast("${action.successCount} of ${action.successCount + action.failureCount} succeeded", ToastKind.Info)
                }
            }

            QueueContent(state = state, onIntent = ::intent, onTapSaveView = { saveViewName = "" })

            pendingConfirm?.let { (kind, config) ->
                AdminConfirmDialog(
                    config = config,
                    onConfirm = { reason ->
                        intent(QueueIntent.ConfirmBulkAction(kind, reason))
                        pendingConfirm = null
                    },
                    onDismiss = { pendingConfirm = null },
                )
            }

            saveViewName?.let { current ->
                AdminModal(onDismiss = { saveViewName = null }) {
                    var name by remember { mutableStateOf(current) }
                    H3 { Text("Save view") }
                    AdminInput(value = name, onChange = { name = it }, placeholder = "View name")
                    Div({ style { display(DisplayStyle.Flex); gap(HandpassSpacing.SM); justifyContent(JustifyContent.FlexEnd); property("margin-top", "${HandpassSpacing.MD}") } }) {
                        AdminButton(label = "Cancel", onClick = { saveViewName = null }, variant = AdminButtonVariant.Ghost)
                        AdminButton(label = "Save", onClick = {
                            if (name.isNotBlank()) {
                                intent(QueueIntent.SaveCurrentView(name))
                                saveViewName = null
                            }
                        }, variant = AdminButtonVariant.Primary)
                    }
                }
            }
        }
    }
}

@Composable
private fun QueueContent(state: QueueState, onIntent: (QueueIntent) -> Unit, onTapSaveView: () -> Unit) {
    AdminPageHeader(
        title = "Report Queue",
        subtitle = queueSubtitle(state),
        actions = {
            AdminButton(
                label = "Save view",
                onClick = onTapSaveView,
                variant = AdminButtonVariant.Ghost,
                size = AdminButtonSize.SM,
                leadingContent = { Icon(WebIcon.Plus, size = 15) },
            )
            AdminButton(
                label = "Refresh",
                onClick = { onIntent(QueueIntent.Refresh) },
                variant = AdminButtonVariant.Primary,
                size = AdminButtonSize.SM,
                leadingContent = { Icon(WebIcon.Refresh, size = 15) },
            )
        },
    )

    QueueStatRow()

    FilterPillRow(state = state, onIntent = onIntent)

    if (state.selectedIds.isNotEmpty()) {
        Div({ style { property("margin-bottom", "${HandpassSpacing.Default}") } }) {
            BulkToolbar(count = state.selectedIds.size, onIntent = onIntent)
        }
    }

    AdminPanel(title = "Open reports", count = state.totalCount.toInt(), flush = true) {
        when (val data = state.data) {
            AsyncSection.Loading -> Div({ style { property("padding", "${HandpassSpacing.Default}") } }) {
                AdminSkeletonTable(rows = 8)
            }
            is AsyncSection.Failed -> AdminEmptyState(
                title = "Failed to load",
                description = data.message,
                actionLabel = "Retry",
                onAction = { onIntent(QueueIntent.Refresh) },
            )
            is AsyncSection.Loaded -> {
                if (data.value.isEmpty()) AdminQueueAllClear()
                else QueueTable(items = data.value, selectedIds = state.selectedIds, onIntent = onIntent)
            }
        }
    }

    Div({ style { property("margin-top", "${HandpassSpacing.XL}") } }) {
        AdminPagination(
            currentPage = state.page,
            totalPages = state.totalPages,
            totalCount = state.totalCount,
            onPageChange = { onIntent(QueueIntent.GoToPage(it)) },
        )
    }
}

private fun queueSubtitle(state: QueueState): String {
    val count = state.totalCount
    val noun = if (count == 1L) "report" else "reports"
    val base = when (state.source) {
        QueueSourceFilter.ACTIVE -> "$count active $noun in review"
        else -> "$count $noun awaiting review"
    }
    val items = when (val d = state.data) {
        is AsyncSection.Loaded -> d.value
        else -> emptyList()
    }
    val high = items.count { isHighPriority(it) }
    return if (high > 0) "$base · $high high priority" else base
}

private fun titleOf(item: AdminQueueItem): String = when (item) {
    is AdminQueueItem.ListingReportItem -> item.listingTitle ?: "(no title)"
    is AdminQueueItem.UserReportItem -> item.reportedUserName ?: "(unknown user)"
    is AdminQueueItem.ActiveListingItem -> item.title
}

private fun typeTagOf(item: AdminQueueItem): String = when (item) {
    is AdminQueueItem.ListingReportItem -> "Listing report"
    is AdminQueueItem.UserReportItem -> "User · ${item.reporterCount} report${if (item.reporterCount == 1) "" else "s"}"
    is AdminQueueItem.ActiveListingItem -> "Active listing"
}

private fun reasonOf(item: AdminQueueItem): String = when (item) {
    is AdminQueueItem.ListingReportItem -> item.reason
    is AdminQueueItem.UserReportItem -> item.reason
    is AdminQueueItem.ActiveListingItem -> "${item.reportCount} reports"
}

private fun reporterOf(item: AdminQueueItem): String? = when (item) {
    is AdminQueueItem.ListingReportItem -> item.reporterName
    is AdminQueueItem.UserReportItem -> item.reporterName
    is AdminQueueItem.ActiveListingItem -> null
}

private fun statusOf(item: AdminQueueItem): Pair<String, AdminBadgeVariant> {
    val reporters = when (item) {
        is AdminQueueItem.ListingReportItem -> item.reporterCount
        is AdminQueueItem.UserReportItem -> item.reporterCount
        is AdminQueueItem.ActiveListingItem -> item.reportCount
    }
    return when {
        item.priorActions > 0 || reporters >= 3 -> "High" to AdminBadgeVariant.Error
        reporters >= 2 -> "Review" to AdminBadgeVariant.Warning
        else -> "Low" to AdminBadgeVariant.Neutral
    }
}

private fun isHighPriority(item: AdminQueueItem): Boolean =
    statusOf(item).second == AdminBadgeVariant.Error

@OptIn(kotlin.time.ExperimentalTime::class)
private fun relativeAge(iso: String): String {
    val instant = runCatching { kotlin.time.Instant.parse(iso) }.getOrNull() ?: return iso.take(10)
    val diff = kotlin.time.Clock.System.now() - instant
    return when {
        diff.inWholeMinutes < 1 -> "now"
        diff.inWholeHours < 1 -> "${diff.inWholeMinutes}m"
        diff.inWholeDays < 1 -> "${diff.inWholeHours}h"
        diff.inWholeDays < 7 -> "${diff.inWholeDays}d"
        else -> "${diff.inWholeDays / 7}w"
    }
}

@Composable
private fun QueueStatRow() {
    val container = koinContainer<StatsContainer>()
    LaunchedEffect(Unit) { container.store.intent(StatsIntent.Refresh) }
    val state by container.store.subscribe { _ -> }

    val s = state
    if (s is StatsState.Error) return

    QueueStatGrid {
        when (s) {
            StatsState.Loading -> repeat(4) { AdminSkeletonCard() }
            is StatsState.Content -> {
                StatCard(
                    label = "Pending reports",
                    value = "${s.stats.totalQueueSize}",
                    icon = WebIcon.Queue,
                    highlight = s.stats.totalQueueSize > 0,
                )
                StatCard(label = "Active users", value = "${s.stats.totalUsers}", icon = WebIcon.Users)
                StatCard(label = "Live listings", value = "${s.stats.activeListings}", icon = WebIcon.Listings)
                StatCard(label = "Resolved today", value = "${s.stats.actionsToday}", icon = WebIcon.Check)
            }
            is StatsState.Error -> Unit
        }
    }
}

@Composable
private fun QueueStatGrid(content: @Composable () -> Unit) {
    val viewportMode = LocalViewportMode.current
    Div({
        style {
            display(DisplayStyle.Grid)
            property(
                "grid-template-columns",
                if (viewportMode == ViewportMode.Mobile) "1fr" else "repeat(auto-fit, minmax(min(170px, 100%), 1fr))",
            )
            gap(HandpassSpacing.Default)
            property("margin-bottom", "${HandpassSpacing.XL}")
        }
    }) { content() }
}

@Composable
private fun FilterPillRow(state: QueueState, onIntent: (QueueIntent) -> Unit) {
    Div({
        style {
            display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(HandpassSpacing.SM)
            property("flex-wrap", "nowrap"); property("overflow-x", "auto"); property("margin-bottom", "${HandpassSpacing.XL}")
        }
    }) {
        QueueSourceFilter.entries.forEach { src ->
            AdminButton(
                label = src.name.lowercase().replaceFirstChar { it.uppercase() },
                onClick = { onIntent(QueueIntent.SetSource(src)) },
                variant = if (state.source == src) AdminButtonVariant.Primary else AdminButtonVariant.Secondary,
                size = AdminButtonSize.SM,
            )
        }
        FilterDivider()
        QueueSortFilter.entries.forEach { sort ->
            AdminButton(
                label = sort.name.replace("_", " ").lowercase().replaceFirstChar { it.uppercase() },
                onClick = { onIntent(QueueIntent.SetSort(sort)) },
                variant = if (state.sort == sort) AdminButtonVariant.Primary else AdminButtonVariant.Secondary,
                size = AdminButtonSize.SM,
            )
        }
        FilterDivider()
        AdminButton(
            label = "Urgent",
            onClick = { onIntent(QueueIntent.SetView(if (state.view == QueueViewFilter.URGENT) null else QueueViewFilter.URGENT)) },
            variant = if (state.view == QueueViewFilter.URGENT) AdminButtonVariant.Destructive else AdminButtonVariant.Secondary,
            size = AdminButtonSize.SM,
        )
        state.savedViews.forEach { v ->
            AdminButton(
                label = v.name,
                onClick = { onIntent(QueueIntent.ApplySavedView(v)) },
                variant = AdminButtonVariant.Secondary,
                size = AdminButtonSize.SM,
            )
        }
    }
}

@Composable
private fun FilterDivider() {
    Span({
        style {
            width(1.px); property("align-self", "stretch"); property("flex-shrink", "0")
            property("min-height", "20px"); property("background-color", Border.value().toString())
        }
    })
}

@Composable
private fun BulkToolbar(count: Int, onIntent: (QueueIntent) -> Unit) {
    AdminCard(paddingScale = AdminCardPadding.Compact) {
        Div({ style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(HandpassSpacing.MD); property("flex-wrap", "nowrap"); property("overflow-x", "auto") } }) {
            Text("$count selected")
            AdminButton(label = "Dismiss", onClick = { onIntent(QueueIntent.TapBulkAction(QueueBulkActionKind.Dismiss)) }, variant = AdminButtonVariant.Ghost, size = AdminButtonSize.SM)
            AdminButton(label = "Suspend listings", onClick = { onIntent(QueueIntent.TapBulkAction(QueueBulkActionKind.SuspendListing)) }, variant = AdminButtonVariant.Destructive, size = AdminButtonSize.SM)
            AdminButton(label = "Warn users", onClick = { onIntent(QueueIntent.TapBulkAction(QueueBulkActionKind.WarnUser)) }, variant = AdminButtonVariant.Secondary, size = AdminButtonSize.SM)
            if (LocalAdminAuth.current.role.isAtLeast(AdminRole.ADMIN)) {
                AdminButton(label = "Suspend users", onClick = { onIntent(QueueIntent.TapBulkAction(QueueBulkActionKind.SuspendUser)) }, variant = AdminButtonVariant.Secondary, size = AdminButtonSize.SM)
                AdminButton(label = "Ban users", onClick = { onIntent(QueueIntent.TapBulkAction(QueueBulkActionKind.BanUser)) }, variant = AdminButtonVariant.Destructive, size = AdminButtonSize.SM)
            }
            AdminButton(label = "Cancel", onClick = { onIntent(QueueIntent.ClearSelection) }, variant = AdminButtonVariant.Ghost, size = AdminButtonSize.SM)
        }
    }
}

@Composable
private fun QueueTable(items: List<AdminQueueItem>, selectedIds: Set<String>, onIntent: (QueueIntent) -> Unit) {
    AdminTable(
        columns = listOf(
            AdminColumn<AdminQueueItem>("Reported item", "2.2fr") { item ->
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        property("gap", "11px")
                        property("min-width", "0")
                    }
                }) {
                    when (item) {
                        is AdminQueueItem.ListingReportItem -> QueueThumb(item.thumbnailUrl)
                        is AdminQueueItem.UserReportItem -> AdminAvatar(name = item.reportedUserName, url = item.reportedUserAvatarUrl, size = AdminAvatarSize.SM)
                        is AdminQueueItem.ActiveListingItem -> QueueThumb(item.thumbnailUrl)
                    }
                    AdminReasonCell(title = titleOf(item), sub = typeTagOf(item))
                }
            },
            AdminColumn<AdminQueueItem>("Reason", "1.3fr") { item ->
                AdminReasonCell(title = reasonOf(item))
            },
            AdminColumn<AdminQueueItem>("Reporter", "1fr") { item ->
                val reporter = reporterOf(item)
                if (reporter != null) {
                    AdminUserCell(name = reporter)
                } else {
                    Span({ style { color(OnSurfaceFaint.value()) } }) { Text("—") }
                }
            },
            AdminColumn<AdminQueueItem>("Age", "70px") { item ->
                AdminNumCell(relativeAge(item.createdAt))
            },
            AdminColumn<AdminQueueItem>("Status", "100px") { item ->
                val status = statusOf(item)
                AdminStatusBadge(label = status.first, variant = status.second)
            },
            AdminColumn<AdminQueueItem>("Actions", "84px") { item ->
                AdminRowActions {
                    AdminIconButton(
                        icon = WebIcon.Eye,
                        label = "View report",
                        onClick = { onIntent(QueueIntent.TapItem(item)) },
                    )
                }
            },
        ),
        rows = items,
        rowId = { it.id },
        onRowClick = { onIntent(QueueIntent.TapItem(it)) },
        selectable = true,
        selectedIds = selectedIds,
        onSelectionChange = { newIds ->
            val toggled = (newIds - selectedIds) + (selectedIds - newIds)
            toggled.forEach { onIntent(QueueIntent.ToggleSelect(it)) }
        },
        flush = true,
        mobileCard = { QueueMobileCard(it) },
    )
}

@Composable
private fun QueueMobileCard(item: AdminQueueItem) {
    Div({
        style {
            display(DisplayStyle.Flex)
            property("gap", "${HandpassSpacing.MD}")
            property("align-items", "flex-start")
        }
    }) {
        when (item) {
            is AdminQueueItem.ListingReportItem -> QueueThumb(item.thumbnailUrl)
            is AdminQueueItem.UserReportItem -> AdminAvatar(name = item.reportedUserName, url = item.reportedUserAvatarUrl, size = AdminAvatarSize.SM)
            is AdminQueueItem.ActiveListingItem -> QueueThumb(item.thumbnailUrl)
        }
        Div({
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                property("flex", "1")
                property("min-width", "0")
                property("gap", "${HandpassSpacing.XXS}")
            }
        }) {
            Span({ style { fontWeight(600); color(OnSurface.value()) } }) {
                Text(
                    when (item) {
                        is AdminQueueItem.ListingReportItem -> item.listingTitle ?: "(no title)"
                        is AdminQueueItem.UserReportItem -> item.reportedUserName ?: "(unknown)"
                        is AdminQueueItem.ActiveListingItem -> item.title
                    }
                )
            }
            Span({ style { fontSize(HandpassTypography.Caption); color(OnSurfaceMuted.value()) } }) {
                Text(
                    when (item) {
                        is AdminQueueItem.ListingReportItem -> "${item.reason} · by ${item.reporterName ?: "?"}"
                        is AdminQueueItem.UserReportItem -> "${item.reason} · by ${item.reporterName ?: "?"}"
                        is AdminQueueItem.ActiveListingItem -> "${item.reportCount} reports"
                    }
                )
            }
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    property("gap", "${HandpassSpacing.XXS}")
                    property("flex-wrap", "wrap")
                    property("margin-top", "${HandpassSpacing.XXS}")
                }
            }) {
                AdminBadge(
                    label = when (item) {
                        is AdminQueueItem.ListingReportItem -> "LISTING"
                        is AdminQueueItem.UserReportItem -> "USER"
                        is AdminQueueItem.ActiveListingItem -> "ACTIVE"
                    },
                    variant = AdminBadgeVariant.Neutral,
                )
                if (item.priorActions > 0) {
                    AdminBadge(label = "${item.priorActions} prior", variant = AdminBadgeVariant.Error)
                }
                val reporters = when (item) {
                    is AdminQueueItem.ListingReportItem -> item.reporterCount
                    is AdminQueueItem.UserReportItem -> item.reporterCount
                    is AdminQueueItem.ActiveListingItem -> item.reportCount
                }
                if (reporters >= 2) {
                    AdminBadge(label = "$reporters reporters", variant = AdminBadgeVariant.Warning)
                }
                Span({
                    style {
                        fontSize(HandpassTypography.Caption)
                        color(OnSurfaceFaint.value())
                        property("margin-left", "auto")
                    }
                }) { Text(item.createdAt.take(10)) }
            }
        }
    }
}

@Composable
private fun QueueThumb(url: String?) {
    url?.let {
        Img(it) {
            style {
                width(ThumbSize.value())
                height(ThumbSize.value())
                property("object-fit", "cover")
                borderRadius(HandpassRadius.SM)
            }
        }
    } ?: Text("")
}

private fun bulkConfirmConfig(kind: QueueBulkActionKind, count: Int): ConfirmConfig = when (kind) {
    QueueBulkActionKind.Dismiss -> ConfirmConfig(title = "Dismiss reports", message = "Dismiss $count selected report(s)?", confirmLabel = "Dismiss")
    QueueBulkActionKind.SuspendListing -> ConfirmConfig(title = "Suspend listings", message = "Suspend $count selected listing(s)?", confirmLabel = "Suspend", isDestructive = true, reasonRequired = true)
    QueueBulkActionKind.WarnUser -> ConfirmConfig(title = "Warn users", message = "Issue warnings to $count user(s)?", confirmLabel = "Warn", reasonRequired = true)
    QueueBulkActionKind.SuspendUser -> ConfirmConfig(title = "Suspend users", message = "Suspend $count user(s) for 7 days?", confirmLabel = "Suspend", isDestructive = true, reasonRequired = true, typeToConfirm = "SUSPEND")
    QueueBulkActionKind.BanUser -> ConfirmConfig(title = "Ban users", message = "Permanently ban $count user(s)?", confirmLabel = "Ban", isDestructive = true, reasonRequired = true, typeToConfirm = "BAN")
}
